/**
 * Content Page
 * Dynamic collection content management
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import {
	Plus,
	Search,
	X,
	Trash2,
	ChevronLeft,
	ChevronRight,
	RefreshCw,
	Loader2,
	Bookmark,
	BookmarkPlus,
	Filter as FilterIcon,
	Database,
	RotateCcw,
	Save,
	Lock,
	Edit3,
} from 'lucide-react';
import { useItems, useDeleteItem, useDeleteItems, useUpdateItems, type PrimaryKey } from '@/hooks/useItems';
import { useFields } from '@/hooks/useFields';
import { useCollection } from '@/hooks/useCollections';
import { useItemsStore, type ItemsQuery } from '@/stores/items';
import { usePresetsStore } from '@/stores/presets';
import { usePreset } from '@/hooks/usePreset';
import { FilterBuilder, type Filter } from '@/components/filters';
import { useRightSidebar, type SidebarSection } from '@/components/sidebar/index';
import { Info, LayoutGrid, RefreshCw as RefreshIcon, Download } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { LayoutWrapper, LayoutOptionsWrapper } from '@/layouts/LayoutWrapper';
import { getAvailableLayouts, type LayoutId } from '@/layouts';
import { expandUserFields } from '@/utils/user-fields';
import { useMyPermissions } from '@/hooks/access-control';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { BulkEditSheet } from '@/components/content/BulkEditSheet';
import { ImportExportSidebar } from '@/components/content/ImportExportSidebar';

export function ContentPage() {
	const { collection } = useParams<{ collection: string }>();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	// Get bookmark ID from URL
	const bookmarkIdFromUrl = useMemo(() => {
		const bookmarkParam = searchParams.get('bookmark');
		return bookmarkParam ? parseInt(bookmarkParam, 10) : null;
	}, [searchParams]);

	// Store state
	const { selectedItems, query, setCollection, setPage, setLimit, setSearch, deselectAll, toggleSelection } = useItemsStore();

	// Use preset for this collection - pass bookmark from URL
	// The usePreset hook will load the bookmark's filter automatically
	const {
		layout,
		setLayout,
		layoutQuery,
		setLayoutQuery,
		layoutOptions,
		allLayoutOptions,
		setLayoutOptions,
		filter: presetFilter,
		setFilter: setPresetFilter,
		saveCurrentAsBookmark,
		bookmarkSaved,
		bookmarkExists,
		editable,
		savePreset: savePresetFromHook,
		clearLocalSave,
		presetReady,
	} = usePreset({
		collection: collection || '',
		bookmark: bookmarkIdFromUrl,
		temporary: !collection,
	});

	// Presets store for bookmarks
	const { bookmarks: presetBookmarks, getBookmark, hydrated: presetsHydrated } = usePresetsStore();

	// Get bookmarks for this collection (from presets store)
	const collectionBookmarks = useMemo(() => {
		return presetBookmarks().filter((b) => b.collection === collection);
	}, [presetBookmarks, collection]);

	const { data: myPermissions } = useMyPermissions();

	// Active bookmark ID is the one from URL
	const activeBookmarkId = bookmarkIdFromUrl;

	// Local state
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [itemToDelete, setItemToDelete] = useState<string | number | null>(null);
	const [bookmarkDialogOpen, setBookmarkDialogOpen] = useState(false);
	const [newBookmarkName, setNewBookmarkName] = useState('');
	const [newBookmarkIcon, setNewBookmarkIcon] = useState('bookmark');
	const [newBookmarkColor, setNewBookmarkColor] = useState<string | null>(null);
	const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
	const [autoRefreshInterval, setAutoRefreshInterval] = useState<number | null>(null);
	const [bulkEditOpen, setBulkEditOpen] = useState(false);

	// Search state — searchInput is the live input value; debouncedSearch is sent to the API.
	// Both are seeded from the Zustand store so they survive navigate-away / navigate-back.
	const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [searchInput, setSearchInput] = useState<string>(() => query.search || '');
	const [debouncedSearch, setDebouncedSearch] = useState<string>(() => query.search || '');
	// Mirror of searchInput kept in a ref so effects/timers always read the
	// latest value without needing it in their dependency arrays.
	const searchInputRef = useRef(searchInput);
	searchInputRef.current = searchInput;
	// Always keep a fresh reference to layoutQuery so the debounce callback never
	// captures a stale value when it fires 300 ms later.
	const layoutQueryRef = useRef(layoutQuery);
	layoutQueryRef.current = layoutQuery;

	// Current layout from preset (default to tabular)
	const currentLayout = (layout || 'tabular') as LayoutId;

	// Set collection on mount/change.
	// setCollection resets query for a *new* collection, so we immediately
	// re-apply whatever is in the search bar — this keeps the search term alive
	// when the user switches collections and when they return from a detail page.
	useEffect(() => {
		if (collection) {
			setCollection(collection);
			// Re-persist the current search into the (possibly reset) store so that
			// a subsequent detail-page visit + back-navigation restores it correctly.
			const current = searchInputRef.current;
			if (current) setSearch(current);
		}
	}, [collection, setCollection]);

	// Fetch collection info
	const { data: collectionInfo, isLoading: collectionLoading } = useCollection(collection || '');

	// Redirect singleton collections directly to item view (form) instead of list
	useEffect(() => {
		if (collection && collectionInfo?.meta?.singleton) {
			navigate(`/admin/content/${collection}/singleton`, { replace: true });
		}
	}, [collection, collectionInfo?.meta?.singleton, navigate]);

	// Fetch fields for the collection (include hidden to allow adding any field)
	const { fields, loading: fieldsLoading } = useFields({
		collection: collection || null,
		includeHidden: true,
	});

	// Archive handling
	const archiveField = collectionInfo?.meta?.archive_field;
	const archiveValue = collectionInfo?.meta?.archive_value ?? 'archived';
	const archiveAppFilter = collectionInfo?.meta?.archive_app_filter;
	const hasArchive = useMemo(() => Boolean(archiveField && archiveAppFilter), [archiveField, archiveAppFilter]);

	const collectionPermissions = useMemo(
		() => (myPermissions || []).filter((p) => p.collection === collection),
		[myPermissions, collection]
	);
	const canCreate = collectionPermissions.some((p) => p.action === 'create' && p.access !== 'none');
	const canRead = collectionPermissions.some((p) => p.action === 'read' && p.access !== 'none');
	const canUpdate = collectionPermissions.some((p) => p.action === 'update' && p.access !== 'none');
	const canDelete = collectionPermissions.some((p) => p.action === 'delete' && p.access !== 'none');

	type ArchiveMode = 'active' | 'archived' | 'all';
	const [archiveMode, setArchiveMode] = useState<ArchiveMode>('active');

	useEffect(() => {
		if (!hasArchive) {
			setArchiveMode('all');
			return;
		}

		if (searchParams.has('archived')) {
			setArchiveMode('archived');
		} else if (searchParams.has('all')) {
			setArchiveMode('all');
		} else {
			setArchiveMode('active');
		}
	}, [hasArchive, searchParams]);

	const updateArchiveMode = (mode: ArchiveMode) => {
		if (!hasArchive) return;
		const params = new URLSearchParams(searchParams);
		params.delete('archived');
		params.delete('all');

		if (mode === 'archived') {
			params.set('archived', '');
		} else if (mode === 'all') {
			params.set('all', '');
		}

		setArchiveMode(mode);
		const search = params.toString();
		navigate({ search: search ? `?${search}` : '' }, { replace: true });
	};

	// Get primary key field
	const primaryKeyField = useMemo(() => {
		return fields.find((f) => f.schema?.is_primary_key)?.field || 'id';
	}, [fields]);

	// Get default fields — first 10 non-system, non-hidden fields
	// Excludes system audit columns (date_created, date_updated, user_created, user_updated, status)
	// so tables open with meaningful data columns by default.
	const SYSTEM_SPECIALS = new Set(['date-created', 'date-updated', 'user-created', 'user-updated']);
	const SYSTEM_FIELD_NAMES = new Set(['date_created', 'date_updated', 'user_created', 'user_updated', 'status']);
	const defaultFields = useMemo(() => {
		const sortedFields = [...fields]
			.filter((f) => {
				if (f.meta?.hidden) return false;
				if (f.meta?.special?.includes('no-data')) return false;
				if (f.meta?.special?.some((s: string) => SYSTEM_SPECIALS.has(s))) return false;
				if (SYSTEM_FIELD_NAMES.has(f.field)) return false;
				return true;
			})
			.sort((a, b) => {
				if (a.schema?.is_primary_key) return -1;
				if (b.schema?.is_primary_key) return 1;
				const aSort = a.meta?.sort ?? 999;
				const bSort = b.meta?.sort ?? 999;
				return aSort - bSort;
			});
		return sortedFields.slice(0, 10).map((f) => f.field);
	}, [fields]);

	// Ensure layout query has a valid field list so the layout can render all columns and allow adding more
	// Guard on presetReady so we never run while usePreset holds stale data from the previous collection.
	useEffect(() => {
		if (!collection) return;
		if (fields.length === 0) return;
		if (!presetReady) return;

		const fieldNames = fields.map((f) => f.field);
		const existingFields = (layoutQuery?.fields || []).filter((f) => fieldNames.includes(f.split('.')[0] || ''));
		const optionFields = Array.isArray((layoutOptions as any)?.fields)
			? (layoutOptions as any).fields.filter((f: string) => fieldNames.includes(f.split('.')[0] || ''))
			: [];

		const initialFields =
			existingFields.length > 0
				? existingFields
				: optionFields.length > 0
					? optionFields
					: defaultFields.filter((f) => fieldNames.includes(f));

		const needsInit = !layoutQuery?.fields || layoutQuery.fields.length === 0;
		const needsCleanup = layoutQuery?.fields && existingFields.length !== layoutQuery.fields.length;

		if (needsInit || needsCleanup) {
			setLayoutQuery({
				...layoutQuery,
				fields: initialFields,
				page: layoutQuery?.page || query.page,
				limit: layoutQuery?.limit || query.limit,
				sort: layoutQuery?.sort || query.sort,
			} as any);
		}
	}, [
		collection,
		fields,
		defaultFields,
		layoutQuery,
		layoutOptions,
		setLayoutQuery,
		query.page,
		query.limit,
		query.sort,
		presetReady,
	]);

	// Use preset fields or default to calculated default fields
	// Filter to only include fields that actually exist in the collection
	// Wait until fields are loaded before determining selection
	const selectedFieldNames = useMemo(() => {
		if (fields.length === 0) return [];

		const fieldNames = fields.map((f) => f.field);

		const candidateFields = layoutQuery?.fields?.length
			? layoutQuery.fields
			: Array.isArray((layoutOptions as any)?.fields)
				? (layoutOptions as any).fields
				: defaultFields;

		const validFields = (candidateFields || []).filter((name: string) => {
			const base = name.split('.')[0];
			return base && fieldNames.includes(base);
		});

		return validFields.length > 0 ? validFields : defaultFields;
	}, [layoutQuery?.fields, layoutOptions, defaultFields, fields]);

	// Build query with fields from preset, including filter
	// Only include specific fields if we have them, otherwise use '*'
	const expandedFields = useMemo(
		() => (selectedFieldNames.length > 0 ? expandUserFields(selectedFieldNames, fields) : ['*']),
		[selectedFieldNames, fields]
	);

	const archiveFilter = useMemo(() => {
		if (!hasArchive || !archiveField) return undefined;
		// Skip archive filter when the archive field no longer exists in the collection
		// (e.g. the column was deleted after the collection metadata was configured).
		if (fields.length > 0 && !fields.some((f) => f.field === archiveField)) return undefined;
		if (archiveMode === 'active') return { [archiveField]: { _neq: archiveValue } } as Record<string, unknown>;
		if (archiveMode === 'archived') return { [archiveField]: { _eq: archiveValue } } as Record<string, unknown>;
		return undefined; // all
	}, [hasArchive, archiveField, archiveValue, archiveMode, fields]);

	const mergeFilters = (base?: Record<string, unknown> | null, extra?: Record<string, unknown>) => {
		if (base && extra) return { _and: [base, extra] } as Record<string, unknown>;
		return (base as Record<string, unknown>) || extra || undefined;
	};

	const queryWithFields = useMemo<ItemsQuery>(() => {
		// Strip sort entries that reference deleted fields so a stale sort never
		// causes a 500 on the API.
		const rawSort = layoutQuery?.sort || query.sort || [];
		const safeSort =
			fields.length > 0
				? rawSort.filter((s) => {
						const base = (s.startsWith('-') ? s.slice(1) : s).split('.')[0];
						return fields.some((f) => f.field === base);
					})
				: rawSort;

		return {
			...query,
			search: debouncedSearch || undefined,
			fields: expandedFields,
			filter: mergeFilters(presetFilter || undefined, archiveFilter),
			sort: safeSort,
			page: layoutQuery?.page || query.page,
			limit: layoutQuery?.limit || query.limit,
		};
	}, [query, expandedFields, presetFilter, layoutQuery, archiveFilter, debouncedSearch, fields]);

	// Only fetch items when we have valid fields loaded AND not a singleton (singletons use item form)
	const shouldFetchItems =
		collection && !collectionInfo?.meta?.singleton && (selectedFieldNames.length > 0 || !fieldsLoading);

	// Fetch items
	const {
		data: itemsData,
		isLoading: itemsLoading,
		refetch,
		isFetching,
	} = useItems(collection || '', queryWithFields, {
		enabled: !!shouldFetchItems,
	});

	// Auto-refresh effect
	useEffect(() => {
		if (!autoRefreshInterval) return;

		const intervalId = setInterval(() => {
			refetch();
		}, autoRefreshInterval);

		return () => clearInterval(intervalId);
	}, [autoRefreshInterval, refetch]);

	const items = itemsData?.items || [];
	const meta = itemsData?.meta;

	// Mutations
	const deleteItem = useDeleteItem(collection || '');
	const deleteItems = useDeleteItems(collection || '');
	const updateItems = useUpdateItems(collection || '');

	// Get active bookmark details - use getBookmark for reliable lookup
	// This ensures we find the bookmark even on direct page load
	const activeBookmark = useMemo(() => {
		if (!activeBookmarkId || !presetsHydrated) return null;
		return getBookmark(activeBookmarkId);
	}, [activeBookmarkId, presetsHydrated, getBookmark]);

	// Check if current state differs from saved bookmark
	// Use bookmarkSaved from usePreset hook which properly tracks changes
	const hasUnsavedChanges = bookmarkExists && editable && !bookmarkSaved;

	// Handlers
	// Handle filter change from FilterBuilder - saves directly to preset
	const handleFilterChange = (filter: Filter | null) => {
		setPresetFilter(filter as Record<string, unknown> | null);
	};

	// Handle search input change with 300 ms debounce.
	// Persists the committed term to the Zustand store so it survives unmount
	// (e.g. opening a detail row) and is restored on the way back.
	// Also resets pagination to page 1 so the first page of results is always shown.
	const handleSearchInputChange = (value: string) => {
		setSearchInput(value);
		if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
		searchTimerRef.current = setTimeout(() => {
			setDebouncedSearch(value);
			setSearch(value); // ← persist to store; also resets store's page to 1
			setLayoutQuery({
				...layoutQueryRef.current,
				page: 1,
				fields: layoutQueryRef.current?.fields,
				sort: layoutQueryRef.current?.sort,
				limit: layoutQueryRef.current?.limit,
			});
		}, 300);
	};

	// Count active filters
	const activeFilterCount = useMemo(() => {
		if (!presetFilter) return 0;
		// Handle both _and and _or at top level
		const filter = presetFilter as Filter | null;
		if (filter?._and && Array.isArray(filter._and)) {
			return filter._and.length;
		}
		if (filter?._or && Array.isArray(filter._or)) {
			return filter._or.length;
		}
		return 0;
	}, [presetFilter]);

	// Create a new bookmark from current view state
	const handleCreateBookmark = async () => {
		if (!collection || !newBookmarkName.trim()) return;

		try {
			const bookmark = await saveCurrentAsBookmark({
				bookmark: newBookmarkName.trim(),
				icon: newBookmarkIcon,
				color: newBookmarkColor,
			});

			if (bookmark && bookmark.id) {
				toast.success('Bookmark created');
				// Navigate to the new bookmark
				navigate(`/admin/content/${collection}?bookmark=${bookmark.id}`);
			}
		} catch (error) {
			toast.error('Failed to create bookmark');
		}

		setNewBookmarkName('');
		setNewBookmarkIcon('bookmark');
		setNewBookmarkColor(null);
		setBookmarkDialogOpen(false);
	};

	// Apply a bookmark - just navigate, usePreset will load the bookmark automatically
	const handleApplyBookmark = (bookmarkId: number) => {
		navigate(`/admin/content/${collection}?bookmark=${bookmarkId}`);
	};

	// Clear bookmark - navigate without bookmark param
	const handleClearBookmark = () => {
		navigate(`/admin/content/${collection}`);
	};

	// Restore bookmark's original filter
	const handleRestoreBookmarkFilter = () => {
		if (!bookmarkExists) return;
		// Use clearLocalSave from usePreset hook to revert to saved bookmark
		clearLocalSave();
		toast.success('Bookmark filter restored');
	};

	// Update bookmark with current filter
	const handleUpdateBookmarkFilter = async () => {
		if (!activeBookmark || !activeBookmark.id) return;
		if (!editable) return;

		try {
			await savePresetFromHook();
			toast.success('Bookmark updated');
		} catch (error) {
			toast.error('Failed to update bookmark');
		}
	};

	const handleCreateItem = () => {
		if (!canCreate) return;
		navigate(`/admin/content/${collection}/+`);
	};

	const handleEditItem = (id: string | number) => {
		if (!canUpdate) return;
		navigate(`/admin/content/${collection}/${id}`);
	};

	// TODO: Will be used by layout row actions
	const _handleDeleteClick = (id: string | number) => {
		setItemToDelete(id);
		setDeleteDialogOpen(true);
	};
	void _handleDeleteClick; // Suppress unused warning

	const handleBatchDeleteClick = () => {
		if (selectedItems.length === 0 || !canDelete) return;
		setItemToDelete(null);
		setDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!canDelete) return;
		try {
			if (itemToDelete !== null) {
				await deleteItem.mutateAsync(itemToDelete);
				toast.success('Item deleted');
			} else if (selectedItems.length > 0) {
				await deleteItems.mutateAsync(selectedItems);
				toast.success(`${selectedItems.length} items deleted`);
				deselectAll();
			}
		} catch (error) {
			toast.error('Failed to delete item(s)');
		} finally {
			setDeleteDialogOpen(false);
			setItemToDelete(null);
		}
	};

	const handleBulkEdit = async (updates: Record<string, any>) => {
		if (selectedItems.length === 0 || !canUpdate) return;

		try {
			await updateItems.mutateAsync({
				keys: selectedItems,
				data: updates,
			});
			toast.success(`${selectedItems.length} items updated`);
			deselectAll();
			refetch();
		} catch (error) {
			toast.error('Failed to update items');
			throw error;
		}
	};

	// Pagination - use effective values from layoutQuery (same source as API call)
	const effectiveLimit = layoutQuery?.limit || query.limit || 25;
	const effectivePage = layoutQuery?.page || query.page || 1;
	const totalPages = meta?.filter_count && effectiveLimit ? Math.ceil(meta.filter_count / effectiveLimit) : 1;
	const currentPage = effectivePage;

	const handlePrevPage = () => {
		if (currentPage > 1) {
			handlePageChange(currentPage - 1);
		}
	};

	const handleNextPage = () => {
		if (currentPage < totalPages) {
			handlePageChange(currentPage + 1);
		}
	};

	// Wrapper functions to update both query and layoutQuery for pagination
	const handlePageChange = (page: number) => {
		setPage(page);
		setLayoutQuery({
			...layoutQuery,
			page,
			fields: layoutQuery?.fields,
			sort: layoutQuery?.sort,
			limit: layoutQuery?.limit,
		});
	};

	const handleLimitChange = (limit: number) => {
		setLimit(limit);
		setLayoutQuery({
			...layoutQuery,
			limit,
			page: 1, // Reset to page 1 when changing limit
			fields: layoutQuery?.fields,
			sort: layoutQuery?.sort,
		});
	};

	// Check if we have records
	const hasRecords = items.length > 0;

	// Register sidebar sections - conditionally include based on records and collection config
	const sidebarSections = useMemo<SidebarSection[]>(() => {
		const sections: SidebarSection[] = [
			{
				id: `info-${collection}`,
				icon: <Info className="h-5 w-5" />,
				title: 'Information',
				defaultOpen: true,
				content: (
					<div className="space-y-4 text-sm">
						{collection && (
							<div>
								<span className="text-muted-foreground">Collection</span>
								<p className="font-medium">{collectionInfo?.meta?.collection || collection}</p>
							</div>
						)}
						{collectionInfo?.meta?.note && (
							<div>
								<span className="text-muted-foreground">Description</span>
								<p className="text-foreground">{collectionInfo.meta.note}</p>
							</div>
						)}
						{meta?.filter_count !== undefined && (
							<div>
								<span className="text-muted-foreground">Items</span>
								<p className="font-medium">{meta.filter_count.toLocaleString()}</p>
							</div>
						)}
					</div>
				),
			},
		];

		// Layout, archive, refresh, and export sections are always visible
		sections.push({
			id: `layout-${currentLayout}-${collection}`,
			icon: <LayoutGrid className="h-5 w-5" />,
			title: 'Layout Options',
			content: (
				<div className="space-y-4">
					<div className="space-y-2">
						<Label className="text-xs text-muted-foreground">Layout</Label>
						<Select value={currentLayout} onValueChange={(v) => setLayout(v)}>
							<SelectTrigger className="w-full">
								<SelectValue placeholder="Select layout" />
							</SelectTrigger>
							<SelectContent>
								{getAvailableLayouts().map((layoutConfig) => (
									<SelectItem key={layoutConfig.id} value={layoutConfig.id}>
										{layoutConfig.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					{/* Layout-specific options */}
					<LayoutOptionsWrapper
						layout={currentLayout}
						layoutOptions={(allLayoutOptions as any) || {}}
						onLayoutOptionsChange={(opts) => setLayoutOptions((opts as any)?.[currentLayout] || null)}
						collection={collection || ''}
						fields={fields}
					/>
				</div>
			),
		});

		// Archive section - only if collection has archive_field configured
		if (hasArchive) {
			sections.push({
				id: `archive-${collection}`,
				icon: <Database className="h-5 w-5" />,
				title: 'Archive',
				content: (
					<div className="space-y-2">
						<div className="flex items-center space-x-2">
							<input
								type="radio"
								id="active"
								name="archive"
								className="h-4 w-4"
								checked={archiveMode === 'active'}
								onChange={() => updateArchiveMode('active')}
							/>
							<Label htmlFor="active" className="text-sm font-normal">
								Show Active Items
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<input
								type="radio"
								id="archived"
								name="archive"
								className="h-4 w-4"
								checked={archiveMode === 'archived'}
								onChange={() => updateArchiveMode('archived')}
							/>
							<Label htmlFor="archived" className="text-sm font-normal">
								Show Archived Items
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<input
								type="radio"
								id="all"
								name="archive"
								className="h-4 w-4"
								checked={archiveMode === 'all'}
								onChange={() => updateArchiveMode('all')}
							/>
							<Label htmlFor="all" className="text-sm font-normal">
								Show All Items
							</Label>
						</div>
					</div>
				),
			});
		}

		sections.push({
			id: `refresh-${collection}`,
			icon: <RefreshIcon className="h-5 w-5" />,
			title: 'Auto Refresh',
			badge: autoRefreshInterval ? '●' : undefined,
			content: (
				<div className="space-y-2">
					{[
						{ value: null, label: 'Off' },
						{ value: 10000, label: '10 seconds' },
						{ value: 30000, label: '30 seconds' },
						{ value: 60000, label: '1 minute' },
						{ value: 300000, label: '5 minutes' },
					].map((option) => (
						<div key={option.value ?? 'off'} className="flex items-center space-x-2">
							<input
								type="radio"
								id={`refresh-${option.value ?? 'off'}`}
								name="refresh"
								checked={autoRefreshInterval === option.value}
								onChange={() => setAutoRefreshInterval(option.value)}
								className="h-4 w-4"
							/>
							<Label htmlFor={`refresh-${option.value ?? 'off'}`} className="text-sm font-normal">
								{option.label}
							</Label>
						</div>
					))}
				</div>
			),
		});

		sections.push({
			id: `export-${collection}`,
			icon: <Download className="h-5 w-5" />,
			title: 'Import / Export',
			content: (
				<ImportExportSidebar
					collection={collection!}
					query={queryWithFields}
					onRefresh={() => refetch()}
					canCreate={canCreate}
					totalCount={meta?.filter_count || 0}
				/>
			),
		});

		return sections;
	}, [
		collection,
		collectionInfo,
		meta?.filter_count,
		currentLayout,
		autoRefreshInterval,
		setLayout,
		layoutOptions,
		setLayoutOptions,
		fields,
		hasArchive,
		hasRecords,
		queryWithFields,
		refetch,
		myPermissions,
	]);

	// Register sidebar content
	useRightSidebar(sidebarSections);

	// Loading state
	const isLoading = collectionLoading || fieldsLoading || itemsLoading;

	if (!collection) {
		return (
			<div className="flex h-full items-center justify-center">
				<div className="text-center">
					<h2 className="text-xl font-semibold text-foreground">No Collection Selected</h2>
					<p className="mt-2 text-muted-foreground">Select a collection from the sidebar to view its content</p>
				</div>
			</div>
		);
	}

	return (
		<div className="flex h-full flex-col">
			{/* Header */}
			<div className="border-b border-border bg-background px-6 py-4">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-4">
						{/* Collection Icon */}
						<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
							<Database
								className="h-5 w-5"
								style={collectionInfo?.meta?.color ? { color: collectionInfo.meta.color } : undefined}
							/>
						</div>
						<div>
							<div className="flex items-center gap-2">
								<h1 className="text-xl font-bold capitalize text-foreground">
									{activeBookmark
										? activeBookmark.bookmark
										: (collectionInfo?.meta?.collection || collection).replace(/_/g, ' ')}
								</h1>
								{/* Bookmark controls */}
								<div className="flex items-center gap-1">
									{!activeBookmark ? (
										<TooltipProvider>
											<Tooltip>
												<TooltipTrigger asChild>
													<Button
														variant="outline"
														size="sm"
														className="h-7 gap-1.5 px-2 border-dashed text-muted-foreground hover:text-primary hover:border-primary"
														onClick={() => setBookmarkDialogOpen(true)}
													>
														<BookmarkPlus className="h-4 w-4" />
														<span className="text-xs">Bookmark</span>
													</Button>
												</TooltipTrigger>
												<TooltipContent>Save current view as a bookmark</TooltipContent>
											</Tooltip>
										</TooltipProvider>
									) : hasUnsavedChanges ? (
										<>
											<Button
												variant="ghost"
												size="icon"
												className="h-7 w-7"
												onClick={handleUpdateBookmarkFilter}
												title="Update bookmark with current filters"
												disabled={!editable}
											>
												<Save className="h-4 w-4 text-muted-foreground hover:text-foreground" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="h-7 w-7"
												onClick={handleRestoreBookmarkFilter}
												title="Restore original bookmark filters"
											>
												<RotateCcw className="h-4 w-4 text-muted-foreground hover:text-foreground" />
											</Button>
										</>
									) : (
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											onClick={handleClearBookmark}
											title="Clear bookmark"
										>
											<Bookmark className="h-4 w-4 text-primary fill-primary" />
										</Button>
									)}
									{activeBookmark && !editable && (
										<span title="Read-only preset">
											<Lock className="h-4 w-4 text-muted-foreground" />
										</span>
									)}
								</div>
							</div>
							<p className="text-sm text-muted-foreground">Content</p>
						</div>
					</div>

					{/* Header Actions */}
					<div className="flex items-center gap-2">
						{/* Filter */}
						<Popover open={filterPopoverOpen} onOpenChange={setFilterPopoverOpen}>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									size="sm"
									className={cn('relative gap-2', activeFilterCount > 0 && 'border-primary text-primary')}
								>
									<FilterIcon className="h-4 w-4" />
									Filter
									{activeFilterCount > 0 && (
										<span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
											{activeFilterCount}
										</span>
									)}
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-fit" align="end">
								<div className="space-y-4">
									<h4 className="font-medium">Filters</h4>
									<FilterBuilder
										value={presetFilter as Filter | null}
										onChange={handleFilterChange}
										fields={fields}
										collection={collection}
									/>
								</div>
							</PopoverContent>
						</Popover>

						{/* Bookmarks dropdown */}
						{collectionBookmarks.length > 0 && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant="outline" size="icon">
										<Bookmark className="h-4 w-4" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									{collectionBookmarks.map((bookmark) => (
										<DropdownMenuItem key={bookmark.id} onClick={() => handleApplyBookmark(bookmark.id!)}>
											<Bookmark
												className={cn('mr-2 h-4 w-4', activeBookmarkId === bookmark.id && 'fill-primary text-primary')}
											/>
											{bookmark.bookmark}
										</DropdownMenuItem>
									))}
								</DropdownMenuContent>
							</DropdownMenu>
						)}

						{/* Batch edit */}
						{selectedItems.length > 0 && (
							<Button
								variant="outline"
								size="icon"
								onClick={() => setBulkEditOpen(true)}
								disabled={!canUpdate}
								title={canUpdate ? 'Bulk edit' : 'Not allowed'}
							>
								<Edit3 className="h-4 w-4" />
							</Button>
						)}

						{/* Batch delete */}
						{selectedItems.length > 0 && (
							<Button
								variant="outline"
								size="icon"
								onClick={handleBatchDeleteClick}
								className="text-destructive hover:text-destructive"
								disabled={!canDelete}
								title={canDelete ? undefined : 'Not allowed'}
							>
								<Trash2 className="h-4 w-4" />
							</Button>
						)}

						{/* Create button */}
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<span>
										<Button
											onClick={handleCreateItem}
											disabled={!canCreate}
											title={canCreate ? undefined : 'Not allowed'}
										>
											<Plus className="h-4 w-4" />
										</Button>
									</span>
								</TooltipTrigger>
								{!canCreate && <TooltipContent>Not allowed</TooltipContent>}
							</Tooltip>
						</TooltipProvider>
					</div>
				</div>
			</div>

			{/* Secondary toolbar - search, selected count and pagination */}
			<div className="flex items-center justify-between border-b border-border bg-muted/30 px-6 py-2">
				<div className="flex items-center gap-3">
					<div className="relative flex items-center">
						<Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
						<Input
							type="text"
							placeholder="Search…"
							value={searchInput}
							onChange={(e) => handleSearchInputChange(e.target.value)}
							className={cn(
								'h-8 w-52 pl-8 pr-7 text-sm',
								searchInput && 'border-primary ring-1 ring-primary/20'
							)}
						/>
						{searchInput && (
							<button
								type="button"
								aria-label="Clear search"
								onClick={() => handleSearchInputChange('')}
								className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
							>
								<X className="h-3.5 w-3.5" />
							</button>
						)}
					</div>

					{/* Item count + refresh */}
					<div className="flex items-center gap-2 text-sm text-muted-foreground">
						{selectedItems.length > 0 ? (
							<span>
								{selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected
							</span>
						) : (
							<span>{meta?.filter_count !== undefined ? `${meta.filter_count} items` : 'Loading…'}</span>
						)}
						<Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => refetch()} disabled={isFetching}>
							<RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
						</Button>
					</div>
				</div>

				<div className="flex items-center gap-3">
					{/* Items per page */}
					<Select value={String(effectiveLimit)} onValueChange={(value) => handleLimitChange(Number(value))}>
						<SelectTrigger className="h-8 w-20">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="10">10</SelectItem>
							<SelectItem value="25">25</SelectItem>
							<SelectItem value="50">50</SelectItem>
							<SelectItem value="100">100</SelectItem>
						</SelectContent>
					</Select>
				</div>
			</div>

			{/* Main content area with sidebar */}
			<div className="flex-1 flex overflow-hidden">
				{/* Content area */}
				<div className="flex-1 flex flex-col overflow-hidden">
					{/* Content - overflow-hidden constrains height so layout can scroll */}
					<div className="flex-1 overflow-hidden">
						{isLoading ? (
							<div className="flex h-full items-center justify-center">
								<Loader2 className="h-8 w-8 animate-spin text-primary" />
							</div>
						) : items.length === 0 ? (
							<div className="flex h-full flex-col items-center justify-center text-center">
								<div className="rounded-full bg-muted p-4">
									<Search className="h-8 w-8 text-muted-foreground" />
								</div>
								<h3 className="mt-4 text-lg font-semibold text-foreground">No items found</h3>
								<p className="mt-1 text-sm text-muted-foreground">
									{debouncedSearch || presetFilter
										? 'Try adjusting your search or filter'
										: 'Get started by creating your first item'}
								</p>
								{!debouncedSearch && !presetFilter && (
									<TooltipProvider>
										<Tooltip>
											<TooltipTrigger asChild>
												<span>
													<Button
														className="mt-4"
														onClick={handleCreateItem}
														disabled={!canCreate}
														title={canCreate ? undefined : 'Not allowed'}
													>
														<Plus className="mr-2 h-4 w-4" />
														Create Item
													</Button>
												</span>
											</TooltipTrigger>
											{!canCreate && <TooltipContent>Not allowed</TooltipContent>}
										</Tooltip>
									</TooltipProvider>
								)}
							</div>
						) : (
							<LayoutWrapper
								layout={currentLayout}
								collection={collection}
								primaryKeyField={primaryKeyField}
								items={items}
								loading={isLoading}
								error={null}
								page={currentPage}
								limit={effectiveLimit}
								totalCount={meta?.filter_count}
								itemCount={items.length}
								totalPages={totalPages}
								onPageChange={handlePageChange}
								onLimitChange={handleLimitChange}
								selection={selectedItems}
								onSelectionChange={(ids) => {
									deselectAll();
									ids.forEach((id) => toggleSelection(id as PrimaryKey));
								}}
								showSelect="multiple"
								sort={layoutQuery?.sort || []}
								onSortChange={(sort) => {
									setLayoutQuery({
										...layoutQuery,
										sort,
										page: layoutQuery?.page || 1,
										fields: layoutQuery?.fields,
									});
								}}
								layoutOptions={(layoutOptions as any) || {}}
								onLayoutOptionsChange={(opts) => setLayoutOptions(opts as any)}
								layoutQuery={(layoutQuery as any) || {}}
								onLayoutQueryChange={(q) => setLayoutQuery(q as any)}
								fields={fields}
								onItemClick={
									canRead || canUpdate ? (item) => handleEditItem(item[primaryKeyField] as PrimaryKey) : undefined
								}
								onRefresh={() => refetch()}
								readonly={!canUpdate}
							/>
						)}
					</div>

					{/* Footer / Pagination */}
					{items.length > 0 && (
						<div className="flex items-center justify-between border-t border-border bg-background px-6 py-3">
							<div className="text-sm text-muted-foreground">
								{meta?.filter_count !== undefined && (
									<>
										Showing {(currentPage - 1) * effectiveLimit + 1} to{' '}
										{Math.min(currentPage * effectiveLimit, meta.filter_count)} of {meta.filter_count} items
									</>
								)}
							</div>
							<div className="flex items-center gap-2">
								<Button variant="outline" size="icon" onClick={handlePrevPage} disabled={currentPage <= 1}>
									<ChevronLeft className="h-4 w-4" />
								</Button>
								<span className="text-sm text-muted-foreground">
									Page {currentPage} of {totalPages}
								</span>
								<Button variant="outline" size="icon" onClick={handleNextPage} disabled={currentPage >= totalPages}>
									<ChevronRight className="h-4 w-4" />
								</Button>
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Delete Confirmation Dialog */}
			<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Are you sure?</AlertDialogTitle>
						<AlertDialogDescription>
							{itemToDelete !== null
								? 'This will permanently delete this item. This action cannot be undone.'
								: `This will permanently delete ${selectedItems.length} items. This action cannot be undone.`}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDeleteConfirm}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							Delete
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Create Bookmark Dialog */}
			<Dialog open={bookmarkDialogOpen} onOpenChange={setBookmarkDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Create Bookmark</DialogTitle>
						<DialogDescription>Save the current filters and search as a bookmark for quick access.</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label htmlFor="bookmark-name">Bookmark Name</Label>
							<Input
								id="bookmark-name"
								placeholder="My Bookmark"
								value={newBookmarkName}
								onChange={(e) => setNewBookmarkName(e.target.value)}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setBookmarkDialogOpen(false)}>
							Cancel
						</Button>
						<Button onClick={handleCreateBookmark} disabled={!newBookmarkName.trim()}>
							Create Bookmark
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Bulk Edit Sheet */}
			<BulkEditSheet
				open={bulkEditOpen}
				onOpenChange={setBulkEditOpen}
				fields={fields}
				selectedCount={selectedItems.length}
				onSave={handleBulkEdit}
			/>
		</div>
	);
}
